<template>
    <div class="record_card" :class="cardClass" @click="$emit('click')">
        <div class="record_card_main">
            <div class="record_card_left" :class="leftClass" :style="leftStyle">
                <div v-if="leftAccentColor" class="fake_border" :style="{ backgroundColor: leftAccentColor }"></div>
                <div v-if="showLeftIcon" class="icon">
                    <ion-icon :icon="icon"></ion-icon>
                </div>
                <div>
                    <div class="title" :class="[titleClass, truncateTitle ? 'truncate_title' : '']">{{ title }}</div>
                    <div v-if="date" class="date" :class="dateClass">{{ date }}</div>
                    <div v-if="secondaryText" class="secondary" :class="secondaryClass">{{ secondaryText }}</div>
                </div>
            </div>

            <div class="record_card_right" :class="rightClass">
                <span v-if="value" class="value" :class="[valueClass, valueAsBadge ? 'badge' : '']">{{ value }}</span>
                <ion-icon v-if="rightIcon" :icon="rightIcon" class="right_icon" :class="rightIconClass"></ion-icon>
                <span v-if="showAlert" class="alert" @click.stop="$emit('alert-click')">
                    {{ alertLabel }}
                </span>
            </div>
        </div>
        <div v-if="$slots.extra" class="record_card_extra">
            <slot name="extra"></slot>
        </div>
    </div>
</template>

<script>
import { defineComponent } from "vue";
import { IonIcon } from "@ionic/vue";

export default defineComponent({
    name: "BaseRecordCard",
    components: {
        IonIcon,
    },
    props: {
        icon: {
            type: [String, Object],
            default: null,
        },
        title: {
            type: String,
            required: true,
        },
        date: {
            type: String,
            required: true,
        },
        secondaryText: {
            type: String,
            default: "",
        },
        value: {
            type: String,
            default: "",
        },
        valueAsBadge: {
            type: Boolean,
            default: false,
        },
        showAlert: {
            type: Boolean,
            default: false,
        },
        showLeftIcon: {
            type: Boolean,
            default: true,
        },
        alertLabel: {
            type: String,
            default: "Anomalia",
        },
        rightIcon: {
            type: [String, Object],
            default: null,
        },
        cardClass: {
            type: String,
            default: "",
        },
        leftClass: {
            type: String,
            default: "",
        },
        rightClass: {
            type: String,
            default: "",
        },
        titleClass: {
            type: String,
            default: "",
        },
        dateClass: {
            type: String,
            default: "",
        },
        valueClass: {
            type: String,
            default: "",
        },
        rightIconClass: {
            type: String,
            default: "",
        },
        truncateTitle: {
            type: Boolean,
            default: false,
        },
        leftWidth: {
            type: String,
            default: "",
        },
        leftAccentColor: {
            type: String,
            default: "",
        },
        secondaryClass: {
            type: String,
            default: "",
        },
    },
    emits: ["click", "alert-click"],
    setup(props) {
        const leftStyle = props.leftWidth ? { width: props.leftWidth } : {};
        return {
            leftStyle,
        };
    },
});
</script>

<style scoped>
.record_card {
    width: 100%;
    display: flex;
    flex-direction: column;
    padding: 10px;
    background: #ffffff;
    border-radius: 8px;
    margin-bottom: 8px;
    box-shadow: 0px 2px 5px 0px rgba(0, 0, 0, 0.25);
}
.record_card_main {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
}

.record_card_left {
    display: flex;
    gap: 8px;
}
.record_card_left .fake_border {
    width: 8px;
    border-radius: 4px;
}

.record_card_left .icon {
    background: #086fa31a;
    width: 40px;
    height: 40px;
    border-radius: 4px;
    display: flex;
    justify-content: center;
    align-items: center;
}

.record_card_left .icon ion-icon {
    font-size: 20px;
    color: #086fa3;
}

.record_card_left .title {
    font-size: 14px;
    font-weight: 600;
    color: #1d1d1b;
    margin-bottom: 8px;
    text-transform: capitalize;
}
.record_card_left .title.truncate_title {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.record_card_left .date {
    font-size: 12px;
    font-weight: 500;
    color: #7f8085;
}
.record_card_left .secondary {
    font-size: 11px;
    color: #7f8085;
    margin-top: 4px;
}

.record_card_right {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 8px;
    font-size: 12px;
    font-weight: 500;
    color: #1d1d1b;
}

.record_card_right .value {
    margin-bottom: 4px;
}
.record_card_right .value.badge {
    font-size: 8px;
    font-weight: 600;
    color: #ffffff;
    padding: 4px 8px;
    border-radius: 4px;
}
.record_card_right .value.badge.badge_success {
    background-color: rgb(220 252 231);
    color: rgb(34 197 94);
}
.record_card_right .value.badge.badge_warning {
    background-color: rgb(254 243 199);
    color: rgb(245 158 11);
}
.record_card_right .value.badge.badge_danger {
    background-color: rgb(254 226 226);
    color: rgb(239 68 68);
}
.record_card_right .value.badge.badge_inserita {
    background-color: rgb(254 243 199);
    color: rgb(245 158 11);
}
.record_card_right .value.badge.badge_rimborsata {
    background-color: rgb(220 252 231);
    color: rgb(34 197 94);
}
.record_card_right .value.badge.badge_rifiutata {
    background-color: rgb(254 226 226);
    color: rgb(239 68 68);
}
.record_card_right .value.badge.badge_accettata {
    background-color: rgb(219 234 254);
    color: rgb(59 130 246);
}

.record_card_right .alert {
    font-size: 10px;
    color: #ef4444;
    background-color: #ffd9d9;
    border-radius: 4px;
    padding: 2px 6px;
}

.record_card_right .right_icon {
    font-size: 20px;
    color: #abb0ba;
}
.record_card_right .right_icon.attachment {
    color: #e36f30;
}

.record_card_extra {
    width: 100%;
    margin-top: 8px;
}

.record_card_left .date.date_expired {
    color: rgb(216, 43, 43) !important;
}

.record_card_left .title.documento_title {
    margin-bottom: 6px;
}
</style>
